import api from './api';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string | null;
  role?: string;
  status?: string;
  needsPasswordSetup?: boolean;
  createdAt?: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

function mapUser(data: Record<string, unknown>): User {
  return {
    id: String(data.id || data._id || ''),
    email: String(data.email || ''),
    firstName: String(data.firstName || ''),
    lastName: String(data.lastName || ''),
    phone: data.phone != null && data.phone !== '' ? String(data.phone) : null,
    role: String(data.role || 'USER'),
    status: data.status ? String(data.status) : undefined,
    needsPasswordSetup: Boolean(data.needsPasswordSetup),
    createdAt: data.createdAt ? String(data.createdAt) : undefined,
  };
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/login', credentials);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return {
      token: response.data.token,
      user: mapUser(response.data.user as unknown as Record<string, unknown>),
    };
  },

  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/register', data);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return {
      token: response.data.token,
      user: mapUser(response.data.user as unknown as Record<string, unknown>),
    };
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get<Record<string, unknown>>('/auth/me');
    return mapUser(response.data);
  },

  logout(): void {
    localStorage.removeItem('token');
  },

  getToken(): string | null {
    return localStorage.getItem('token');
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    const response = await api.post(`/auth/reset-password/${token}`, { password });
    return response.data;
  },

  async verifyResetToken(token: string): Promise<{ valid: boolean }> {
    const response = await api.get(`/auth/verify-reset-token/${token}`);
    return response.data;
  },

  async magicLogin(token: string): Promise<AuthResponse> {
    const response = await api.post<AuthResponse>('/auth/magic', { token });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return {
      token: response.data.token,
      user: mapUser(response.data.user as unknown as Record<string, unknown>),
    };
  },

  async setPassword(password: string): Promise<{ message: string; user: User }> {
    const response = await api.post<{ message: string; user: Record<string, unknown> }>(
      '/auth/set-password',
      { password }
    );
    return {
      message: response.data.message,
      user: mapUser(response.data.user),
    };
  },

  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ message: string; user: User }> {
    const response = await api.post<{ message: string; user: Record<string, unknown> }>(
      '/auth/change-password',
      { currentPassword, newPassword }
    );
    return {
      message: response.data.message,
      user: mapUser(response.data.user),
    };
  },
};
